import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Mail, Linkedin, Calendar, ArrowRight, MapPin, Phone, Clock } from 'lucide-react';
import { faqs } from '../mock';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    industry: '',
    message: '',
    preferredTime: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Store in localStorage for mock
    const existingLeads = JSON.parse(localStorage.getItem('leads') || '[]');
    existingLeads.push({ ...formData, timestamp: new Date().toISOString(), type: 'contact_form' });
    localStorage.setItem('leads', JSON.stringify(existingLeads));
    
    toast({
      title: "Message Sent Successfully!",
      description: "We'll get back to you within 24 hours.",
    });
    
    setFormData({ name: '', email: '', company: '', phone: '', industry: '', message: '', preferredTime: '' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#002029] via-[#00303D] to-[#004052] text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Let's Talk About Your Business
            </h1>
            <p className="text-xl text-gray-300">
              Ready to bring structure, clarity, and growth readiness to your financial operations? Get in touch with our team.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 border-gray-200 hover:border-[#00607A] transition-all duration-300 text-center">
              <CardHeader>
                <div className="mb-4 inline-block p-3 bg-[#002029] rounded-lg mx-auto">
                  <Mail className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-lg text-[#002029]">Email Us</CardTitle>
              </CardHeader>
              <CardContent>
                <a href="mailto:admin@venuraconsultants.com" className="text-[#00607A] hover:underline font-medium">
                  admin@venuraconsultants.com
                </a>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-gray-200 hover:border-[#00607A] transition-all duration-300 text-center">
              <CardHeader>
                <div className="mb-4 inline-block p-3 bg-[#002029] rounded-lg mx-auto">
                  <Linkedin className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-lg text-[#002029]">Connect on LinkedIn</CardTitle>
              </CardHeader>
              <CardContent>
                <a href="https://www.linkedin.com/company/108459329/" target="_blank" rel="noopener noreferrer" className="text-[#00607A] hover:underline font-medium">
                  Venura Consultants
                </a>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-gray-200 hover:border-[#00607A] transition-all duration-300 text-center">
              <CardHeader>
                <div className="mb-4 inline-block p-3 bg-[#002029] rounded-lg mx-auto">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-lg text-[#002029]">Response Time</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 font-medium">Within 24 hours</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Main Contact Form */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#002029] mb-4">
                Send Us a Message
              </h2>
              <p className="text-lg text-gray-600">
                Fill out the form below and we'll get back to you as soon as possible
              </p>
            </div>
            
            <Card className="border-2 border-gray-200 shadow-xl">
              <CardContent className="pt-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="John Doe"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="john@company.com"
                        className="w-full"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                      <Input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                        placeholder="Your Company Ltd."
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+1 (555) 000-0000"
                        className="w-full"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Industry/Business Type</label>
                    <Input
                      type="text"
                      name="industry"
                      value={formData.industry}
                      onChange={handleInputChange}
                      placeholder="e.g., SaaS, E-commerce, Agency, Fintech"
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tell Us About Your Needs *</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder="Describe your current challenges, what you're looking to achieve, and any specific questions you have..."
                      rows={6}
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Time for Call</label>
                    <Input
                      type="text"
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleInputChange}
                      placeholder="e.g., Weekdays 10am-2pm EST"
                      className="w-full"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-[#00607A] hover:bg-[#005066] text-white py-6 text-lg transition-all duration-300 transform hover:scale-105"
                  >
                    Send Message
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Schedule Meeting Section */}
      <section className="py-16 bg-gradient-to-br from-[#002029] to-[#004052] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="mb-6 inline-block p-4 bg-white/10 rounded-xl backdrop-blur-sm">
              <Calendar className="h-12 w-12 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Schedule a Consultation
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Prefer to book a time directly? Use our calendar to schedule a consultation at your convenience.
            </p>
            <a 
              href="https://calendly.com/venuraconsultants/30min" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button 
                size="lg" 
                className="bg-[#00607A] hover:bg-[#005066] text-white px-12 py-6 text-lg transition-all duration-300 transform hover:scale-105"
              >
                <Calendar className="mr-3 h-6 w-6" />
                Schedule a Call
              </Button>
            </a>
            <p className="text-gray-300 mt-6">
              Or email us at <a href="mailto:admin@venuraconsultants.com" className="text-[#00607A] hover:underline font-semibold">admin@venuraconsultants.com</a>
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#002029] mb-4">
              Common Questions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Quick answers to questions you may have
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-white border-2 border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-[#002029] hover:text-[#00607A] py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 pb-4 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;